import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Calendar, Copy, Download, Link2, MapPin, Trophy } from "lucide-react";
import { useTeamNameDisplayStore } from "../state/useTeamNameDisplayStore";
import { getTeamDisplayName, getLocalizedStadiumName } from "../utils/teamNameDisplay";
import { formatLocationLabel } from "../utils/locationDisplay";
import { cupRoundLabel } from "../lib/competition/cupRounds";
import type { RoundKey } from "../lib/competition/roundBuckets";
import { groupPublicCalendar, publicCupRoundsMap, type PublicCupRoundRow } from "./publicCalendarRounds";

type CalendarMatch = {
  id: string;
  game_day: number | null;
  /** calendar-export ≥ cup rounds (spec §7.2); absent from an older deployment. */
  is_external_competition?: boolean | null;
  cup_round?: string | null;
  match_date: string | null;
  match_time: string | null;
  status: string | null;
  home_score: number | null;
  away_score: number | null;
  home_team: { id: string; name: string; name_ar: string | null; logo_url: string | null } | null;
  away_team: { id: string; name: string; name_ar: string | null; logo_url: string | null } | null;
  stadium: {
    id: string;
    name: string;
    name_ar: string | null;
    location_id: string | null;
    commune: string | null;
    commune_ar: string | null;
    wilaya: string | null;
    wilaya_ar: string | null;
  } | null;
  league_group: {
    id: string;
    name: string;
    name_ar: string | null;
    season: string;
    kind?: string | null;
    league: { id: string; name: string; name_ar: string | null; code: string } | null;
  } | null;
};

type ApiResponse = { title: string; matches: CalendarMatch[]; cup_rounds?: PublicCupRoundRow[]; error?: string };

const SUPABASE_URL =
  (typeof import.meta !== "undefined" &&
    (import.meta as unknown as { env?: Record<string, string> }).env?.VITE_SUPABASE_URL) ||
  "";

function parseTarget(): { groupId?: string; teamId?: string; season?: string } | null {
  const path = window.location.pathname;
  const search = new URLSearchParams(window.location.search);

  const groupMatch = path.match(/\/c\/([0-9a-f-]{36})/i);
  if (groupMatch) return { groupId: groupMatch[1] };

  const teamMatch = path.match(/\/c\/team\/([0-9a-f-]{36})/i);
  if (teamMatch) {
    const season = search.get("season") ?? undefined;
    return { teamId: teamMatch[1], season };
  }

  const groupQ = search.get("group_id");
  if (groupQ) return { groupId: groupQ };
  const teamQ = search.get("team_id");
  if (teamQ) return { teamId: teamQ, season: search.get("season") ?? undefined };

  return null;
}

function formatDate(d: string | null, locale: string): string {
  if (!d) return "—";
  try {
    return new Date(d + "T00:00:00").toLocaleDateString(locale, {
      weekday: "short", day: "numeric", month: "short", year: "numeric",
    });
  } catch {
    return d;
  }
}

function statusBadge(s: string | null, t: (key: string) => string): { text: string; cls: string } {
  switch (s) {
    case "completed": return { text: t("public_calendar.status_completed"), cls: "bg-emerald-500/15 text-emerald-200 border-emerald-500/30" };
    case "live":      return { text: t("public_calendar.status_live"), cls: "bg-rose-500/15 text-rose-200 border-rose-500/30 animate-pulse" };
    case "postponed": return { text: t("public_calendar.status_postponed"), cls: "bg-amber-500/15 text-amber-200 border-amber-500/30" };
    case "cancelled": return { text: t("public_calendar.status_cancelled"), cls: "bg-white/10 text-white/60 border-white/15" };
    default:           return { text: t("public_calendar.status_scheduled"), cls: "bg-sky-500/15 text-sky-200 border-sky-500/30" };
  }
}

const PublicCalendarPage: React.FC = () => {
  const { t, i18n } = useTranslation("settings");
  const dateLocale = i18n.language === "ar" ? "ar-DZ" : "fr-FR";
  const displayMode = useTeamNameDisplayStore((s) => s.displayMode);
  const [data, setData] = useState<ApiResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState<"page" | "ics" | null>(null);

  const target = useMemo(parseTarget, []);

  const jsonUrl = useMemo(() => {
    if (!target) return null;
    const u = new URL(SUPABASE_URL + "/functions/v1/calendar-export");
    u.searchParams.set("format", "json");
    if (target.groupId) u.searchParams.set("group_id", target.groupId);
    if (target.teamId)  u.searchParams.set("team_id", target.teamId);
    if (target.season)  u.searchParams.set("season", target.season);
    return u.toString();
  }, [target]);

  const icsUrl = useMemo(() => {
    if (!target) return null;
    const u = new URL(SUPABASE_URL + "/functions/v1/calendar-export");
    u.searchParams.set("format", "ics");
    if (target.groupId) u.searchParams.set("group_id", target.groupId);
    if (target.teamId)  u.searchParams.set("team_id", target.teamId);
    if (target.season)  u.searchParams.set("season", target.season);
    return u.toString();
  }, [target]);

  const webcalUrl = useMemo(() => icsUrl?.replace(/^https?:/, "webcal:") ?? null, [icsUrl]);

  useEffect(() => {
    if (!jsonUrl) {
      setError(t("public_calendar.invalid_url"));
      setLoading(false);
      return;
    }
    fetch(jsonUrl)
      .then((r) => r.json())
      .then((j: ApiResponse) => {
        if (j.error) setError(j.error);
        else setData(j);
      })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, [jsonUrl]);

  // Rounds come with the matches (calendar-export reads cup_rounds): no other read as anon.
  const cupRounds = useMemo(() => publicCupRoundsMap(data?.cup_rounds), [data]);
  const grouped = useMemo(
    () => (data?.matches ? groupPublicCalendar(data.matches, cupRounds) : []),
    [data, cupRounds]
  );
  const roundLang: "fr" | "ar" = i18n.language === "ar" ? "ar" : "fr";
  const sectionTitle = (key: RoundKey): string => {
    if (key.kind === "cup") {
      return cupRoundLabel(key.round, cupRounds, roundLang) ?? t("common:cup.round_unknown");
    }
    return key.gameDay > 0 ? t("public_calendar.game_day", { day: key.gameDay }) : t("public_calendar.no_game_day");
  };

  const copy = (kind: "page" | "ics", value: string) => {
    navigator.clipboard.writeText(value);
    setCopied(kind);
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <header className="border-b border-white/10 bg-slate-950/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <Trophy className="h-5 w-5 text-emerald-400" />
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wide text-white/40">{t("public_calendar.header_eyebrow")}</div>
            <div className="text-base font-semibold truncate">{data?.title ?? t("public_calendar.loading_title")}</div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Subscribe banner */}
        {!loading && !error && icsUrl && (
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3">
            <div className="text-sm font-medium text-white/80">{t("public_calendar.subscribe_title")}</div>
            <div className="flex flex-wrap items-center gap-2">
              {webcalUrl && (
                <a
                  href={webcalUrl}
                  className="inline-flex items-center gap-2 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/30 text-emerald-100 px-3 py-1.5 text-sm transition"
                >
                  <Calendar className="h-4 w-4" />
                  {t("public_calendar.add_to_agenda")}
                </a>
              )}
              <a
                href={icsUrl}
                className="inline-flex items-center gap-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 px-3 py-1.5 text-sm transition"
              >
                <Download className="h-4 w-4" />
                {t("public_calendar.download_ics")}
              </a>
              <button
                onClick={() => copy("ics", icsUrl)}
                className="inline-flex items-center gap-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 px-3 py-1.5 text-sm transition"
              >
                <Link2 className="h-4 w-4" />
                {copied === "ics" ? t("public_calendar.copied") : t("public_calendar.copy_ics_url")}
              </button>
              <button
                onClick={() => copy("page", window.location.href)}
                className="inline-flex items-center gap-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 px-3 py-1.5 text-sm transition"
              >
                <Copy className="h-4 w-4" />
                {copied === "page" ? t("public_calendar.copied") : t("public_calendar.copy_page_link")}
              </button>
            </div>
            <p className="text-[11px] text-white/40">
              {t("public_calendar.auto_update_note")}
            </p>
          </div>
        )}

        {loading && (
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-6 text-center text-white/60">
            {t("public_calendar.loading_calendar")}
          </div>
        )}

        {error && (
          <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-6 text-rose-100">
            {error}
          </div>
        )}

        {!loading && !error && data && grouped.length === 0 && (
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-6 text-center text-white/60">
            {t("public_calendar.no_matches")}
          </div>
        )}

        {grouped.map(({ id, key, matches: items }) => (
          <section key={id} className="space-y-2">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-white/40 px-1">
              {sectionTitle(key)}
            </h2>
            <div className="rounded-xl border border-white/10 bg-white/[0.03] divide-y divide-white/5">
              {items.map((m) => {
                const sb = statusBadge(m.status, t);
                return (
                  <article key={m.id} className="px-3 sm:px-4 py-3 grid grid-cols-1 sm:grid-cols-[140px_1fr_auto] gap-2 sm:gap-4 items-center">
                    <div className="text-[11px] text-white/50 space-y-0.5">
                      <div className="font-mono">{formatDate(m.match_date, dateLocale)}</div>
                      {m.match_time && <div className="font-mono">{m.match_time.slice(0, 5)}</div>}
                    </div>
                    <div className="flex items-center gap-3 min-w-0">
                      <TeamSide team={m.home_team} side="home" displayMode={displayMode} />
                      <div className="text-center text-white/40 text-xs font-mono shrink-0 w-12">
                        {m.home_score != null && m.away_score != null
                          ? `${m.home_score} – ${m.away_score}`
                          : "vs"}
                      </div>
                      <TeamSide team={m.away_team} side="away" displayMode={displayMode} />
                    </div>
                    <div className="flex flex-col items-end gap-1 text-[11px]">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full border ${sb.cls}`}>
                        {sb.text}
                      </span>
                      {m.stadium && (
                        <div className="flex items-start gap-1 text-white/50 max-w-[12rem] min-w-0">
                          <MapPin className="h-3 w-3 mt-0.5 shrink-0" />
                          <div className="min-w-0 text-end">
                            <div dir="auto" className="truncate">{getLocalizedStadiumName(m.stadium, displayMode)}</div>
                            {formatLocationLabel(m.stadium, displayMode) && (
                              <div dir="auto" className="text-[10px] text-white/40 truncate">
                                {formatLocationLabel(m.stadium, displayMode)}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        ))}
      </main>

      <footer className="max-w-5xl mx-auto px-4 py-8 text-center text-[11px] text-white/30">
        {t("public_calendar.footer_credit")}
      </footer>
    </div>
  );
};

const TeamSide: React.FC<{
  team: CalendarMatch["home_team"];
  side: "home" | "away";
  displayMode: "arabic" | "french";
}> = ({ team, side, displayMode }) => (
  <div className={`flex items-center gap-2 min-w-0 flex-1 ${side === "away" ? "justify-end text-end flex-row-reverse" : ""}`}>
    {team?.logo_url ? (
      <img src={team.logo_url} alt="" className="h-6 w-6 rounded-full object-cover bg-white/5 shrink-0" />
    ) : (
      <div className="h-6 w-6 rounded-full bg-white/5 border border-white/10 shrink-0" />
    )}
    <span className="text-sm truncate min-w-0">{team ? getTeamDisplayName(team, displayMode) : "—"}</span>
  </div>
);

export default PublicCalendarPage;
